import { Controller } from "@hotwired/stimulus"

// web_push — the "Enable notifications" affordance in the Messenger inbox.
//
// States (mutually exclusive targets; the controller shows exactly one):
//   * enable   — supported + not yet subscribed → the "Enable notifications" button
//   * on       — already subscribed → "Notifications on"
//   * iosHint  — iOS Safari NOT installed to the home screen → "Install to your
//                home screen…" (web push only works there on iOS)
//   * (hidden) — unsupported browser, OR the VAPID key isn't configured (no meta
//                tag) → the whole control stays hidden (its initial state)
//
// The public application-server key is read from <meta name="vapid-public-key">,
// which the server only renders when WebPushConfig.configured?. No key → we bail
// and leave everything hidden, so the UI degrades gracefully with no VAPID set.
export default class extends Controller {
  static targets = ["enable", "on", "iosHint", "status"]

  connect() {
    this.#hideAll()

    if (!this.#supported) {
      // iOS Safari that isn't installed can't do web push at all — nudge the
      // user to install first. Otherwise the browser simply can't, stay hidden.
      if (this.#isIos && !this.#isStandalone) this.#showIosHint()
      return
    }

    // Reflect the current subscription state without prompting for permission.
    this.#refreshState()
  }

  // Click handler on the "Enable notifications" button.
  async enable() {
    if (!this.#supported || !this.#publicKey) return

    this.#setStatus("Enabling…")
    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.#setStatus("Notifications are blocked in your browser settings.")
        return
      }

      const registration = await navigator.serviceWorker.ready
      let subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.#urlBase64ToUint8Array(this.#publicKey),
        })
      }

      await this.#post(subscription)
      this.#showOn()
    } catch (error) {
      this.#setStatus("Couldn't enable notifications. Please try again.")
    }
  }

  // --- state -----------------------------------------------------------------

  async #refreshState() {
    // iOS: web push only works once installed to the home screen.
    if (this.#isIos && !this.#isStandalone) {
      this.#showIosHint()
      return
    }

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription && Notification.permission === "granted") {
        // Best-effort resync so a re-install / server-side purge re-registers.
        this.#post(subscription).catch(() => {})
        this.#showOn()
      } else {
        this.#showEnable()
      }
    } catch (error) {
      this.#showEnable()
    }
  }

  #showEnable() {
    this.#hideAll()
    if (this.hasEnableTarget) this.#show(this.enableTarget)
  }

  #showOn() {
    this.#hideAll()
    if (this.hasOnTarget) this.#show(this.onTarget)
  }

  #showIosHint() {
    this.#hideAll()
    if (this.hasIosHintTarget) this.#show(this.iosHintTarget)
  }

  // --- server ----------------------------------------------------------------

  async #post(subscription) {
    const json = subscription.toJSON()
    const response = await fetch("/push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.#csrfToken,
      },
      body: JSON.stringify({
        subscription: {
          endpoint: subscription.endpoint,
          p256dh: json.keys && json.keys.p256dh,
          auth: json.keys && json.keys.auth,
        },
      }),
    })
    if (!response.ok) throw new Error(`push subscribe failed: ${response.status}`)
  }

  // --- feature detection -------------------------------------------------------

  get #supported() {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window &&
      Boolean(this.#publicKey)
    )
  }

  get #publicKey() {
    const meta = document.querySelector('meta[name="vapid-public-key"]')
    return meta ? meta.content : null
  }

  get #csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  get #isIos() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
  }

  get #isStandalone() {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      navigator.standalone === true
    )
  }

  // --- DOM helpers -------------------------------------------------------------

  // Reaching any terminal state also clears a transient status line ("Enabling…")
  // so it can't linger after the toggle resolves (it made "Notifications on" look
  // stuck on "Enabling…"). Error statuses are set WITHOUT calling #hideAll, so
  // they're preserved.
  #hideAll() {
    this.#setStatus("")
    const els = [
      this.hasEnableTarget && this.enableTarget,
      this.hasOnTarget && this.onTarget,
      this.hasIosHintTarget && this.iosHintTarget,
    ]
    els.forEach((el) => {
      if (el) {
        // Toggle inline display, not just the `hidden` class: these controls carry
        // `inline-flex`, which wins the CSS cascade over `.hidden` (it's emitted
        // later), so adding `hidden` alone never hid them. An inline style always
        // wins, so show/hide is reliable regardless of utility order.
        el.classList.add("hidden")
        el.style.display = "none"
      }
    })
  }

  #show(el) {
    if (el) {
      el.classList.remove("hidden")
      el.style.display = ""
    }
  }

  #setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  // VAPID keys are URL-safe base64; the Push API needs a Uint8Array.
  #urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = atob(base64)
    const output = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i)
    return output
  }
}
